import { ErrorHandler, Injectable, Injector } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { MatDialog } from '@angular/material/dialog';
import { AlertType } from '../../shared/models/alertType';
import { showAlertDialog } from '../../shared/helpers/showAlertDialog';

/**
 * Handler global de erros NÃO-HTTP (erros de JS: null deref, template, promise
 * rejeitada). Erros HTTP são donos do `errorInterceptor` — que já mostra o modal —,
 * então aqui eles são apenas logados, pra não recriar o duplo-modal.
 *
 * Sem sink remoto por ora (a audiência é admin interno e o backend já loga o HTTP).
 * Este handler tem dois papéis: evitar a tela-branca em erro de JS (mostra um modal
 * amigável) e ser o SEAM pra plugar telemetria depois (ex.: POST /client-errors →
 * Serilog), sem refactor.
 */
@Injectable()
export class GlobalErrorHandler implements ErrorHandler {
  private modalOpen = false;

  constructor(private readonly injector: Injector) {}

  handleError(error: unknown): void {
    // Substituímos o ErrorHandler padrão do Angular, que logava no console — manter.
    // SEAM: o report remoto entraria aqui.
    console.error(error);

    // Erro HTTP já foi apresentado pelo errorInterceptor; não duplicar o modal.
    if (this.isHttpError(error)) {
      return;
    }

    // Erro de JS genuíno → modal amigável (anti tela-branca), de-duplicado.
    if (this.modalOpen) {
      return;
    }

    const dialog = this.injector.get(MatDialog, null);
    if (!dialog) {
      return;
    }

    this.modalOpen = true;
    const ref = showAlertDialog(
      dialog,
      AlertType.Error,
      'Ops!',
      'Ocorreu um erro inesperado. Por favor, recarregue a página e tente novamente.',
    );
    ref.afterClosed().subscribe(() => (this.modalOpen = false));
  }

  private isHttpError(error: unknown): boolean {
    return error instanceof HttpErrorResponse
      || (error as { rejection?: unknown })?.rejection instanceof HttpErrorResponse;
  }
}
